import { BoxSlotTypeOf } from "@/modules/boxSlot/boxSlotTypes"
import type {
  BoxSessionCreateInputDto,
  BoxSessionDetailsDto,
  BoxSessionUpdateInputDto,
} from "@/modules/boxSlotSession/gateway/boxSlotSessionDto"
import { UserEntity } from "@/modules/profile/userProfile/userEntity"
import { SessionBlockEntityFactory, type SessionBlockEntityType } from "@/modules/sessions/entities/sessionBlockEntity"
import { SocialPostResumeLightEntity } from "@/modules/social/entities/socialEntity"

export type BoxSessionDetailsState = Omit<BoxSessionDetailsEntity, "toCreateInputDto" | "toUpdateInputDto">

export class BoxSessionDetailsEntity {
  id: string
  name: string
  date: Date | null
  programId: string
  description: string
  blocks: SessionBlockEntityType[]
  owner: UserEntity | null
  hideSessionContent: boolean
  gymnasticTag: boolean
  weightliftingTag: boolean
  cardioTag: boolean
  socialPostId: string | null
  socialResume: SocialPostResumeLightEntity | null
  _type: typeof BoxSlotTypeOf

  private constructor(state: BoxSessionDetailsState) {
    this.id = state.id
    this.name = state.name
    this.date = state.date
    this.programId = state.programId
    this.description = state.description
    this.blocks = state.blocks
    this.owner = state.owner
    this.hideSessionContent = state.hideSessionContent
    this.gymnasticTag = state.gymnasticTag
    this.weightliftingTag = state.weightliftingTag
    this.cardioTag = state.cardioTag
    this._type = state._type
    this.socialPostId = state.socialPostId
    this.socialResume = state.socialResume
  }

  static fromDto(dto: BoxSessionDetailsDto): BoxSessionDetailsEntity {
    return new BoxSessionDetailsEntity({
      _type: BoxSlotTypeOf,
      id: dto.sessionId,
      name: dto.name,
      date: dto.date ? new Date(dto.date) : null,
      programId: dto.programId,
      description: dto.description ?? "",
      blocks: dto.blocks.map((block) => SessionBlockEntityFactory.fromDto(block)),
      owner: dto.owner ? UserEntity.fromDto(dto.owner) : null,
      hideSessionContent: dto.hideSessionContent ?? false,
      gymnasticTag: dto.gymnasticTag,
      weightliftingTag: dto.weightliftingTag,
      cardioTag: dto.cardioTag,
      socialPostId: dto.socialPostId,
      socialResume: dto.socialResume ? SocialPostResumeLightEntity.fromDto(dto.socialResume) : null,
    })
  }

  toCreateInputDto(programId: string): BoxSessionCreateInputDto {
    return {
      programId: programId,
      name: this.name,
      description: this.description,
      blocks: this.blocks.map((block) => SessionBlockEntityFactory.fromEntityToInputDto(block)),
      hideSessionContent: this.hideSessionContent,
      weightliftingTag: this.weightliftingTag,
      gymnasticTag: this.gymnasticTag,
      cardioTag: this.cardioTag,
    }
  }

  toUpdateInputDto(): BoxSessionUpdateInputDto {
    return {
      name: this.name,
      description: this.description,
      blocks: this.blocks.map((block) => SessionBlockEntityFactory.fromEntityToInputDto(block)),
      hideSessionContent: this.hideSessionContent,
      weightliftingTag: this.weightliftingTag,
      gymnasticTag: this.gymnasticTag,
      cardioTag: this.cardioTag,
    }
  }
}
